import {
  CrudRequestCondition,
  CrudRequestParams,
  CrudRequestPrimitiveValue,
  CrudRequestSearchOperator,
} from '../services/pagination/models/interfaces/crud';
import { PaginationDto } from '../services/pagination/models/interfaces/pagination-dto';

function normalizeValue(value: unknown): string {
  if (value instanceof Date) {
    return value.toISOString().toLowerCase();
  }

  if (value === null || value === undefined) {
    return '';
  }

  switch (typeof value) {
    case 'string':
      return value.toLowerCase();
    case 'number':
    case 'boolean':
    case 'bigint':
    case 'symbol':
      return value.toString().toLowerCase();
    default:
      return JSON.stringify(value).toLowerCase();
  }
}

function matchesEqualityOperator(fieldValue: unknown, operator: CrudRequestSearchOperator, normalizedField: string): boolean | undefined {
  if (operator.$eq !== undefined) {
    return normalizeValue(operator.$eq) === normalizedField || fieldValue === operator.$eq;
  }

  if (operator.$ne !== undefined) {
    return normalizeValue(operator.$ne) !== normalizedField && fieldValue !== operator.$ne;
  }

  if (operator.$eqL !== undefined) {
    return normalizedField === normalizeValue(operator.$eqL);
  }

  if (operator.$neL !== undefined) {
    return normalizedField !== normalizeValue(operator.$neL);
  }

  return undefined;
}

function matchesCollectionOperator(fieldValue: unknown, operator: CrudRequestSearchOperator, normalizedField: string): boolean | undefined {
  if (operator.$in !== undefined) {
    return operator.$in.some(item => normalizeValue(item) === normalizedField || item === fieldValue);
  }

  if (operator.$notin !== undefined) {
    return !operator.$notin.some(item => normalizeValue(item) === normalizedField || item === fieldValue);
  }

  if (operator.$inL !== undefined) {
    return operator.$inL.some(item => normalizedField === normalizeValue(item));
  }

  return undefined;
}

function matchesStringOperator(normalizedField: string, operator: CrudRequestSearchOperator): boolean | undefined {
  if (operator.$cont !== undefined) {
    return normalizedField.includes(normalizeValue(operator.$cont));
  }

  if (operator.$contL !== undefined) {
    return normalizedField.includes(normalizeValue(operator.$contL));
  }

  if (operator.$starts !== undefined || operator.$startsL !== undefined) {
    return normalizedField.startsWith(normalizeValue(operator.$starts ?? operator.$startsL));
  }

  if (operator.$ends !== undefined || operator.$endsL !== undefined) {
    return normalizedField.endsWith(normalizeValue(operator.$ends ?? operator.$endsL));
  }

  return undefined;
}

function matchesNullOperator(fieldValue: unknown, operator: CrudRequestSearchOperator): boolean | undefined {
  if (operator.$isnull === true) {
    return fieldValue === null || fieldValue === undefined;
  }

  if (operator.$notnull === true) {
    return fieldValue !== null && fieldValue !== undefined;
  }

  return undefined;
}

function matchesNumericOperator(fieldValue: unknown, operator: CrudRequestSearchOperator): boolean | undefined {
  if (operator.$gt === undefined && operator.$gte === undefined && operator.$lt === undefined && operator.$lte === undefined) {
    return undefined;
  }

  const numericField = Number(fieldValue);
  if (Number.isNaN(numericField)) {
    return false;
  }

  if (operator.$gt !== undefined && numericField <= Number(operator.$gt)) return false;
  if (operator.$gte !== undefined && numericField < Number(operator.$gte)) return false;
  if (operator.$lt !== undefined && numericField >= Number(operator.$lt)) return false;
  if (operator.$lte !== undefined && numericField > Number(operator.$lte)) return false;
  return true;
}

function matchesOperator(fieldValue: unknown, operator: CrudRequestSearchOperator): boolean {
  const normalizedField = normalizeValue(fieldValue);

  return matchesEqualityOperator(fieldValue, operator, normalizedField)
    ?? matchesCollectionOperator(fieldValue, operator, normalizedField)
    ?? matchesStringOperator(normalizedField, operator)
    ?? matchesNullOperator(fieldValue, operator)
    ?? matchesNumericOperator(fieldValue, operator)
    ?? true;
}

function matchesFieldCondition(item: Record<string, unknown>, field: string, condition: unknown): boolean {
  const fieldValue = item[field];

  if (condition === undefined) {
    return true;
  }

  if (condition !== null && typeof condition === 'object' && !Array.isArray(condition) && !(condition instanceof Date)) {
    return matchesOperator(fieldValue, condition as CrudRequestSearchOperator);
  }

  return fieldValue === condition || normalizeValue(fieldValue) === normalizeValue(condition as CrudRequestPrimitiveValue);
}

function matchesCondition<T>(item: T, condition?: CrudRequestCondition<T>): boolean {
  if (!condition) {
    return true;
  }

  const record = condition as Record<string, unknown>;

  if (Array.isArray(record['$and'])) {
    return (record['$and'] as CrudRequestCondition<T>[]).every(inner => matchesCondition(item, inner));
  }

  if (Array.isArray(record['$or'])) {
    return (record['$or'] as CrudRequestCondition<T>[]).some(inner => matchesCondition(item, inner));
  }

  return Object.entries(record).every(([field, value]) => {
    if (field === '$and' || field === '$or') {
      return true;
    }

    return matchesFieldCondition(item as Record<string, unknown>, field, value);
  });
}

export function applyMockSearch<T>(items: T[], search?: CrudRequestCondition<T>): T[] {
  if (!search) {
    return [...items];
  }

  return items.filter(item => matchesCondition(item, search));
}

function compareSortValues(left: unknown, right: unknown, order: 'ASC' | 'DESC'): number {
  if (left === right) {
    return 0;
  }

  if (left === undefined || left === null) {
    return order === 'ASC' ? -1 : 1;
  }

  if (right === undefined || right === null) {
    return order === 'ASC' ? 1 : -1;
  }

  const comparison = left < right ? -1 : 1;
  return order === 'ASC' ? comparison : -comparison;
}

export function applyMockSort<T>(
  items: T[],
  sort?: { field: string; order: 'ASC' | 'DESC' }[],
): T[] {
  if (!sort?.length) {
    return items;
  }

  const sorted = [...items];

  sorted.sort((a, b) => {
    for (const rule of sort) {
      const left = (a as Record<string, unknown>)[rule.field];
      const right = (b as Record<string, unknown>)[rule.field];
      const comparison = compareSortValues(left, right, rule.order);

      if (comparison !== 0) {
        return comparison;
      }
    }

    return 0;
  });

  return sorted;
}

export function applyMockCrudParams<T>(items: T[], params: CrudRequestParams<T> = {}): T[] {
  const filtered = applyMockSearch(items, params.search);
  return applyMockSort(filtered, params.sort as { field: string; order: 'ASC' | 'DESC' }[] | undefined);
}

export function buildMockPagination<T>(
  items: T[],
  page = 1,
  limit = 10,
  total = items.length,
): PaginationDto<T> {
  const safeLimit = Math.max(limit, 1);
  const pageCount = Math.max(Math.ceil(total / safeLimit), 1);

  return {
    data: items,
    page,
    count: items.length,
    total,
    pageCount,
  };
}

export function paginateMockItems<T>(
  items: T[],
  page = 1,
  limit = 10,
): PaginationDto<T> {
  const start = (Math.max(page, 1) - 1) * Math.max(limit, 1);
  const data = items.slice(start, start + Math.max(limit, 1));
  return buildMockPagination(data, page, limit, items.length);
}
